import { posthog } from "posthog-js";
import type { ComponentType } from "preact";
import { useLocation } from "preact-iso";
import { useEffect, useState } from "preact/hooks";
import { toast } from "react-hot-toast";
import {
  Button,
  CtaButton,
  inputClass,
  Logo,
  PageSpinner,
} from "./components.tsx";
import { auth, useAuth } from "./db.ts";
import { usePageMeta } from "./pageMeta.ts";
import { builderChatPath } from "./paths.ts";
import { fireAnalyticsEventLegacy } from "./utils.ts";

type VoidProps = Record<string, never>;

const MagicCode = ({ sentEmail }: { sentEmail: string }) => {
  const [code, setCode] = useState("");
  const { route } = useLocation();
  return (
    <div>
      <h2 className="text-text-primary-light dark:text-text-primary font-body">
        Okay, we sent you an email! What was the code?
      </h2>
      <h3 className="text-text-secondary-light dark:text-text-secondary text-sm mt-1">
        if you don't see it, check your spam folder.
      </h3>
      <div className="mt-3">
        <input
          type="tel"
          inputmode="numeric"
          pattern="[0-9]*"
          placeholder="123456..."
          value={code}
          onInput={(e) => setCode(e.currentTarget.value)}
          className={inputClass}
        />
      </div>
      <Button
        onClick={() => {
          auth.signInWithMagicCode({ email: sentEmail, code })
            .then(() => {
              toast.success("Logged in!");
              const params = new URLSearchParams(globalThis.location?.search);
              if (!params.has("redirect_to")) {
                route(builderChatPath);
              }
            })
            .catch(
              (err: { body?: { message: string } }) => {
                toast.error(`Uh oh :${err.body?.message}`);
                setCode("");
              },
            );
        }}
        type="button"
        className="mt-4"
      >
        Verify
      </Button>
    </div>
  );
};

const authButtonBase =
  "mt-2 px-6 py-3 rounded-lg font-medium text-center w-full max-w-none self-stretch flex items-center justify-center gap-2 text-base shadow-sm transition-all duration-200 cursor-pointer font-body";

const LoginWithEmail = (
  { setSentEmail, onCancel }: {
    setSentEmail: (email: string) => void;
    onCancel: () => void;
  },
) => {
  const [email, setEmail] = useState("");
  return (
    <div>
      <div className="mt-3">
        <input
          placeholder="Enter your email"
          type="email"
          value={email}
          onInput={(e) => setEmail(e.currentTarget.value)}
          className={inputClass}
        />
      </div>
      <div className="flex gap-2 mt-3">
        <Button
          type="button"
          onClick={() => {
            if (!email) return;
            setSentEmail(email);
            auth.sendMagicCode({ email }).catch(
              (err: { body?: { message: string } }) => {
                toast.error(`Uh oh :${err.body?.message}`);
                setSentEmail("");
              },
            );
          }}
          className="flex-1 bg-success hover:bg-success-hover border-success"
        >
          Send Code
        </Button>
        <Button
          type="button"
          onClick={() => {
            onCancel();
            setEmail("");
          }}
          className="flex-1 bg-surface-overlay-light dark:bg-surface-overlay text-text-primary-light dark:text-text-primary hover:bg-border-subtle-light dark:hover:bg-border-subtle"
        >
          Cancel
        </Button>
      </div>
    </div>
  );
};

const AuthButtons = ({
  showGuest = true,
  buttonTextPrefix = "Log in",
  initialMethod = "none",
}: {
  showGuest?: boolean;
  buttonTextPrefix?: string;
  initialMethod?: "none" | "email" | "guest";
}) => {
  const [loginMethod, setLoginMethod] = useState<"none" | "email" | "guest">(
    initialMethod,
  );
  const [sentEmail, setSentEmail] = useState("");
  const { route } = useLocation();
  const googleUrl = auth.createAuthorizationURL({
    clientName: "google-web",
    redirectURL: globalThis.location.href,
  });

  const handleGuestLogin = () => {
    toast.promise(
      auth.signInAsGuest().then(() => {
        const params = new URLSearchParams(globalThis.location?.search);
        if (!params.has("redirect_to")) {
          route(builderChatPath);
        }
      }),
      {
        loading: "Signing in as guest...",
        success: "Logged in as guest!",
        error: (err: { body?: { message: string } }) =>
          `Error: ${err.body?.message || "Failed to sign in as guest"}`,
      },
    );
  };

  return (
    <>
      <div className="flex flex-col gap-3 w-full">
        {loginMethod === "none" && (
          <>
            {showGuest && (
              <div className="flex flex-col gap-1">
                <Button
                  className={`${authButtonBase} bg-[#7c5cbf] hover:bg-[#6b4aae] text-white`}
                  onClick={() => setLoginMethod("guest")}
                  type="button"
                >
                  <span>⚡</span>
                  Quick Start - {buttonTextPrefix} as Guest
                </Button>
                <div className="text-xs text-text-tertiary-light dark:text-text-tertiary text-center">
                  No email required
                </div>
              </div>
            )}
            <Button
              className={`${authButtonBase} bg-[#c44040] hover:bg-[#b33636] text-white`}
              onClick={() => {
                globalThis.location.href = googleUrl;
              }}
            >
              <span>🔍</span>
              {buttonTextPrefix} with Google
            </Button>
            <div className="flex flex-col gap-1">
              <Button
                className={`${authButtonBase} bg-success hover:bg-success-hover text-surface`}
                onClick={() => setLoginMethod("email")}
                type="button"
              >
                <span>📧</span>
                {buttonTextPrefix} with Email
              </Button>
              <div className="text-xs text-text-tertiary-light dark:text-text-tertiary text-center">
                If you don't have Gmail
              </div>
            </div>
          </>
        )}
      </div>
      <div className="w-full">
        {loginMethod === "email" && (
          sentEmail ? <MagicCode sentEmail={sentEmail} /> : (
            <LoginWithEmail
              setSentEmail={setSentEmail}
              onCancel={() => {
                setLoginMethod("none");
                setSentEmail("");
              }}
            />
          )
        )}
        {loginMethod === "guest" && showGuest && (
          <GuestLoginWarning
            onAccept={handleGuestLogin}
            onCancel={() => setLoginMethod("none")}
          />
        )}
      </div>
    </>
  );
};

const GuestLoginWarning = ({ onAccept, onCancel }: {
  onAccept: () => void;
  onCancel: () => void;
}) => (
  <div className="text-left">
    <h3 className="text-lg font-medium mb-3 text-text-primary-light dark:text-text-primary font-display">
      Guest Mode
    </h3>
    <div className="text-sm text-text-secondary-light dark:text-text-secondary mb-4">
      <ul className="list-disc list-inside space-y-1.5">
        <li>You get 100 free messages in your first month</li>
        <li>
          After your first month, add an email to keep using your account
        </li>
        <li>Everything will be saved while you use this browser</li>
        <li>
          If you change browsers or clear data, your information might be gone
        </li>
        <li>You can convert your guest account to an email account later</li>
      </ul>
    </div>
    <div className="flex gap-2">
      <Button
        onClick={onAccept}
        type="button"
        className="flex-1"
      >
        Continue as guest
      </Button>
      <Button
        onClick={onCancel}
        type="button"
        className="flex-1 bg-surface-overlay-light dark:bg-surface-overlay text-text-primary-light dark:text-text-primary border border-border-default-light dark:border-border-default hover:bg-border-subtle-light dark:hover:bg-border-subtle"
      >
        Cancel
      </Button>
    </div>
  </div>
);

const authCardStyle =
  "w-full max-w-md bg-surface-raised-light dark:bg-surface-raised rounded-xl border border-border-subtle-light dark:border-border-subtle shadow-lg";

const LoginForm = () => {
  const { url } = useLocation();
  const params = new URL(url, globalThis.location.origin).searchParams;
  const guestRequested = params.get("guest") === "1";
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flexGrow: 1,
      }}
    >
      <div
        style={{
          display: "flex",
          padding: 25,
          justifyContent: "center",
          alignItems: "stretch",
          flexDirection: "column",
          gap: 10,
        }}
        className={authCardStyle}
      >
        <Logo className="text-4xl md:text-5xl mb-2" clickable={false} />
        <AuthButtons initialMethod={guestRequested ? "guest" : "none"} />
      </div>
    </div>
  );
};

export const WithLogin = (Component: ComponentType<VoidProps>) => () => {
  const { isLoading, user, error } = useAuth();
  if (isLoading) return <PageSpinner />;
  if (error) {
    return <div className="text-danger">Uh oh! {error.message}</div>;
  }
  return (
    <div className="flex flex-col gap-6 flex-1 min-h-0">
      {!user ? <LoginForm /> : <Component />}
    </div>
  );
};

export const loginPath = "/login";

export const LoginCta = (
  { text, className }: { text: string; className?: string },
) => {
  const route = useLocation().route;
  const signedIn = !!useAuth().user;
  return (
    <CtaButton
      onClick={() => {
        fireAnalyticsEventLegacy("signin_cta_click");
        posthog.capture("Signin CTA Click");
        if (signedIn) route(builderChatPath);
        else route(loginPath);
      }}
      text={text}
      className={className}
    />
  );
};

const UpgradeForm = () => {
  const { route } = useLocation();
  const { user } = useAuth();

  useEffect(() => {
    if (user?.email) {
      route(builderChatPath);
    }
  }, [user?.email]);

  if (!user) {
    route(loginPath);
    return null;
  }

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flexGrow: 1,
      }}
    >
      <div
        style={{
          display: "flex",
          padding: 25,
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
          gap: 10,
        }}
        className={authCardStyle}
      >
        <Logo className="text-4xl md:text-5xl mb-2" clickable={false} />
        <h2 className="text-xl font-display font-semibold mb-4 text-text-primary-light dark:text-text-primary">
          Upgrade Your Account
        </h2>
        <p className="text-text-secondary-light dark:text-text-secondary text-center mb-4">
          Convert your guest account to a permanent account with email login
        </p>
        <AuthButtons showGuest={false} buttonTextPrefix="Upgrade" />
        <Button
          className="mt-4 text-sm text-text-tertiary-light dark:text-text-tertiary bg-transparent hover:bg-surface-overlay-light dark:hover:bg-surface-overlay"
          onClick={() => route(builderChatPath)}
          type="button"
        >
          Continue as guest
        </Button>
      </div>
    </div>
  );
};

export const LoginPage = () => {
  usePageMeta({
    title: "Log in | prompt2bot",
    description: "Log in to prompt2bot to build and manage your AI agents.",
  });
  const { user } = useAuth();
  const signedIn = !!user;
  const route = useLocation().route;
  useEffect(() => {
    if (signedIn) {
      const params = new URLSearchParams(globalThis.location?.search);
      const redirectTo = params.get("redirect_to");
      if (redirectTo) {
        const redirectUrl = new URL(redirectTo);
        if (user?.refresh_token) {
          redirectUrl.searchParams.set("token", user.refresh_token);
        }
        globalThis.location.href = redirectUrl.toString();
      } else {
        route(builderChatPath);
      }
    }
  }, [signedIn, user]);
  return <LoginForm />;
};

export const UpgradePage = () => {
  usePageMeta({
    title: "Upgrade your account | prompt2bot",
    description:
      "Convert your guest account to a permanent prompt2bot account.",
  });
  return <UpgradeForm />;
};
